import {
  ContentDisplayStyle,
  DataLayerParameterValues,
  useMapContext,
  ViewType,
} from './Map.tsx';
import cn from '../utils/cn.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faChevronDown,
  faChevronUp,
  faEyeSlash,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/free-solid-svg-icons';
import { DataLayerData } from '../api/api.ts';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Plot from '@/map/Plot.tsx';
import Loading from '@/common/Loading.tsx';
import { LanguageContext } from '@/common/Language.tsx';

export type LayerAndParams = {
  layer: DataLayerData;
  params: DataLayerParameterValues;
};
export default function PanelPlots({
  layerAndParams,
  propagateSize,
}: {
  layerAndParams: LayerAndParams | null;
  propagateSize: (large?: boolean) => void;
}) {
  const { t } = useContext(LanguageContext);
  const { view } = useMapContext();

  const [large, setLarge] = useState(false);
  const [hide, setHide] = useState(false);

  const [plots, setPlots] = useState<LayerAndParams[]>([]);
  const [plotIndex, setPlotIndex] = useState(0);
  const canGoBack = useMemo(() => plotIndex > 0, [plots, plotIndex]);
  const canGoNext = useMemo(
    () => plotIndex < plots.length - 1,
    [plots, plotIndex]
  );

  const currentPlot = useMemo(() => {
    if (plots.length === 0 || !plots[plotIndex]) return null;
    return plots[plotIndex];
  }, [plots, plotIndex]);

  const [loading, setLoading] = useState(false);

  const delayTimer = useRef<null | {
    timeout: number;
    layerAndParams: LayerAndParams;
  }>(null);

  useEffect(() => {
    if (layerAndParams && !layerAndParams.layer.plots) return;
    if (!layerAndParams) {
      if (plots.length > 0) {
        // a way to reset
        setHide(true);
      }
      return;
    }
    setLoading(true);
    if (delayTimer.current !== null) {
      clearTimeout(delayTimer.current.timeout);
      delayTimer.current = null;
    }
    setHide(false);
    const timeout = window.setTimeout(() => {
      if (delayTimer.current === null) return;
      setPlots([delayTimer.current.layerAndParams, ...plots]);
      if (delayTimer.current.layerAndParams.layer.plots!.length > 1) {
        setLarge(true);
      }
      setPlotIndex(0);
      setLoading(false);
    }, 2000);
    delayTimer.current = { timeout: timeout, layerAndParams: layerAndParams };
  }, [layerAndParams]);

  useEffect(() => {
    if (
      hide ||
      (view &&
        view.type === ViewType.Regular &&
        view.contentStyle === ContentDisplayStyle.Full) ||
      plots.length === 0
    ) {
      propagateSize();
    } else {
      propagateSize(large);
    }
  }, [large, plots, hide, view]);

  return (
    <div
      className={cn(
        'absolute',
        'left-1/2',
        'bottom-[30px]',
        '-translate-x-1/2',
        'shadow-lg',
        'rounded-[12px]',
        'z-999',
        'text-sm',
        'bg-gray-100',
        'transition-[width]',
        'transition-[height]',
        'p-[12px]',
        hide || (!loading && plots.length === 0)
          ? 'h-auto'
          : large
            ? 'h-[350px]'
            : 'h-[250px]',
        'overflow-y-hidden',
        'transition-all',
        view &&
          (view.type === ViewType.Story ||
            (view.type === ViewType.Regular &&
              view.contentStyle === ContentDisplayStyle.Full))
          ? 'translate-y-[100vh]'
          : null,
        'flex',
        'items-stretch',
        'justify-center'
      )}
      style={{
        width: large
          ? 'calc( 100vw - ( 4 * 30px ) - ( 2 * 270px) )'
          : 'calc( min( 600px, 100vw - ( 4 * 30px ) - ( 2 * 270px) ) )',
      }}
    >
      {hide && (
        <div
          className={cn(
            'flex-1',
            '-m-[12px]',
            'p-[12px]',
            'text-center',
            'cursor-pointer',
            'hover:bg-radial',
            'hover:from-gray-100',
            'hover:to-gray-200',
            'cursor-pointer'
          )}
          onClick={() => {
            setHide(false);
          }}
        >
          <FontAwesomeIcon icon={faChartBar} />
          &nbsp;
          <span className={cn('whitespace-nowrap')}>
            {t('CLICK TO REVEAL PLOTS', 'KLICKEN UM PLOTS ZU ZEIGEN')}
          </span>
        </div>
      )}
      {!hide && !loading && plots.length === 0 && (
        <div className={cn('text-center')}>
          {t(
            'Trigger a model plot through the controls in the legend on the left for corresponding datasets',
            'Lösen Sie für passende Projekte einen Modell Plot über die Konfigurationselemente auf der linken Seite aus'
          )}
        </div>
      )}
      {!hide && !loading && plots.length > 0 && (
        <>
          {currentPlot &&
            currentPlot.layer.plots!.map((plot, plotIndexInLayer) => (
              <div key={plotIndexInLayer} className={cn('flex-1', 'relative')}>
                {' '}
                <Plot
                  url={plot.url}
                  parameterValues={plots[plotIndex].params}
                  parameters={plots[plotIndex].layer.parameters}
                />
              </div>
            ))}
        </>
      )}
      {!hide && loading && (
        <div className={cn('flex-1', 'flex', 'items-center')}>
          <Loading size={1} center={true} />
        </div>
      )}
      {!hide && (loading || plots.length > 0) && (
        <div
          className={cn(
            'flex-0',
            'flex',
            'flex-col',
            '-my-[12px]',
            '-mr-[12px]',
            'text-center',
            'border-l-1',
            'border-l-gray-200',
            'rounded-[12px]'
          )}
        >
          <div
            className={cn(
              'flex-1',
              'p-[12px]',
              'border-b-1',
              'border-b-gray-200',
              'border-l-1',
              'border-l-white',
              canGoBack
                ? [
                    'cursor-pointer',
                    'hover:bg-radial',
                    'hover:from-gray-200',
                    'hover:to-gray-100',
                  ]
                : 'text-gray-300',
              'flex',
              'items-center',
              'justify-center'
            )}
            onClick={() => {
              if (canGoBack)
                setPlotIndex((currentIndex) => Math.max(0, currentIndex - 1));
            }}
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </div>
          <div
            className={cn(
              'flex-1',
              'p-[12px]',
              'border-t-1',
              'border-t-white',
              'border-l-1',
              'border-l-white',
              'border-b-1',
              'border-b-gray-200',
              'flex',
              'items-center',
              'justify-center',
              'cursor-pointer',
              'hover:bg-radial',
              'hover:from-gray-200',
              'hover:to-gray-100'
            )}
            onClick={() => {
              setLarge((state) => !state);
            }}
          >
            <FontAwesomeIcon
              icon={large ? faMagnifyingGlassMinus : faMagnifyingGlassPlus}
            />
          </div>
          <div
            className={cn(
              'flex-1',
              'p-[12px]',
              'border-t-1',
              'border-t-white',
              'border-l-1',
              'border-l-white',
              'border-b-1',
              'border-b-gray-200',
              'flex',
              'items-center',
              'justify-center',
              'cursor-pointer',
              'hover:bg-radial',
              'hover:from-gray-200',
              'hover:to-gray-100'
            )}
            onClick={() => {
              setHide(true);
            }}
          >
            <FontAwesomeIcon icon={faEyeSlash} />
          </div>
          <div
            className={cn(
              'flex-1',
              'p-[12px]',
              'border-t-1',
              'border-t-white',
              'border-l-1',
              'border-l-white',
              canGoNext
                ? [
                    'cursor-pointer',
                    'hover:bg-radial',
                    'hover:from-gray-200',
                    'hover:to-gray-100',
                  ]
                : 'text-gray-300',
              'flex',
              'items-center',
              'justify-center'
            )}
            onClick={() => {
              if (canGoNext)
                setPlotIndex((currentIndex) =>
                  Math.min(plots.length - 1, currentIndex + 1)
                );
            }}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </div>
        </div>
      )}
    </div>
  );
}
